import { navigationRef } from 'components/Navigation/navigationRef';
import { ModalScreens, Screens } from 'components/Navigation/types';
import { CameraView } from 'expo-camera';
import { hapticSelection } from 'lib/withHapticFeedback';
import { Linking, Platform } from 'react-native';
import { apiGet, apiPost, checkInternetConnection } from 'service/api';
import {
  getDocument,
  getImage,
  getPhoto,
  readAsDataURLAsync,
  removeDataBase64Prefix,
  resizeImage,
} from 'service/file';
import {
  FileSourceType,
  IMAGE_COMPRESSION,
  IMAGE_MAX_HEIGHT,
  IMAGE_MAX_WIDTH,
} from 'service/file/types';
import { hasCameraPermission } from 'service/permission';
import {
  doAssetSaveFilesToAsset,
  doReportingSetSavedPages,
  INIT_BG_LOOP,
  SET_APP_INSTANCE,
} from 'store/actions';
import {
  ALERT,
  ALERT_ERROR,
  CLOSE_BARCODE_SCANNER,
  doFilePreviewPdf,
  doFileShare,
  doFileShareDirect,
  DO_IMAGE_SELECT,
  DO_NETWORK_CHECK_UNTIL_ONLINE,
  eventBarcodeSelected,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  OPEN_BARCODE_SCANNER,
  OPEN_BARCODE_VIEWER,
  OPEN_NATIVE_APP_SETTINGS,
  SHOW_BLOCKING_LOADER,
  UNLOAD_BARCODE_SCANNER,
  UPGRADE,
  doSetSettingSavePhotosToMediaLibrary,
  doAppMerge,
  doShowSyncModal,
  doHideSyncModal,
  doAppFileGetFilesFromPicker,
  doAppFileGetImagesFromPicker,
  eventFilesAdded,
  doShowLoading,
  doLoaderCancel,
  doSetAppType,
  doSetOfflineOnly,
} from 'store/app/actions';
import {
  doAuthShowAuthModal,
  RESET_DEV,
  RESET_DEV_DATA,
} from 'store/auth/actions';
import { CATCH_ERROR } from 'store/error/actions';
import { CHECK_OFFLINE_DELAY } from 'store/sync/constants';
import {
  call,
  put,
  race,
  select,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga';
import { EPFileResponse, EPFileResponsePoll } from 'types/Endpoints/Generic';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { API_FILE_GENERIC, apiFileGenericPoll } from 'service/api/constants';
import { isCancelled, isError, isNetworkError } from 'lib/isError';
import {
  selectUserFeatureCanViewAssessmentsTab,
  selectUserFeatureCanViewReportingTab,
  selectUserId,
} from 'store/auth/selectors';
import * as MediaLibrary from 'expo-media-library';
import { str } from 'lib/str';
import { setUser as sentrySetUser } from '@sentry/react-native';
import { getScreenSize, randomKey, sanitizeFilename } from 'lib/riskbase';
import { sagaWaitForCancel } from 'store/sagaHelpers';
import delay from 'delay';
import {
  selectAppInstance,
  selectBarCodeScanner,
  selectModalsDiscarded,
  selectOfflineOnly,
  selectSettingSavePhotosToMediaLibrary,
} from './selectors';
import { ScreenSize } from 'lib/constants';
import { ICONS } from 'components/Icon';
import { PageEnum, PageTypeEnum } from 'store/remote/types';
import { EPReportsGetPages } from 'types/Endpoints/Reports';

/**
 * Will poll for remote file for a set number of seconds
 * If the file becomes available the function returns
 * If the file errors an error is thrown
 * If the timeout is reached an error is thrown
 */
function* pollForFile(response: EPFileResponse) {
  try {
    const waitKey = randomKey();
    yield* put(
      doShowLoading({
        text: response.pollMessage || 'This may take a while',
        waitKey,
      }),
    );
    sagaWaitForCancel(waitKey);
    yield* race({
      poll: (function* fn() {
        let checkAgain = true;
        do {
          const pollResponse = yield* apiGet<EPFileResponsePoll>(
            apiFileGenericPoll(response.filename),
          );
          if (pollResponse.exists) {
            checkAgain = false;
          } else {
            yield delay(1000);
          }
        } while (checkAgain);
        return true;
      })(),
      delay: (function* fn() {
        yield delay((response.pollTimeoutSeconds || 300) * 1000);
        throw new Error(response.pollTimeoutMessage || 'File unavailable');
      })(),
      cancel: sagaWaitForCancel(waitKey),
    });
    yield* put(HIDE_BLOCKING_LOADER());
  } catch (error) {
    yield* put(HIDE_BLOCKING_LOADER());
    throw error;
  }
}

export const appSideEffects = [
  takeEvery(UPGRADE, function* f() {
    yield* put(
      doSetAppType(
        getScreenSize().size === ScreenSize.small &&
          (Platform.OS === 'ios' || Platform.OS === 'android')
          ? 'mobile'
          : 'full',
      ),
    );

    // Upgrading from a version where offlineOnly is not set so determine it
    // This will only happen once
    const offlineOnly = yield* select(selectOfflineOnly);
    if (offlineOnly === undefined) {
      const hasAssessments = yield* select(
        selectUserFeatureCanViewAssessmentsTab,
      );
      const hasReporting = yield* select(selectUserFeatureCanViewReportingTab);
      yield* put(doSetOfflineOnly(!hasAssessments && !hasReporting));
      // Set saved pages
      const pages: EPReportsGetPages['response']['pages'] = [
        {
          id: 'downloadedProperties',
          title: 'Downloaded Properties',
          description:
            'Perform assessments without an internet connection and sync them later. ' +
            'Download a property to get started',
          menuDescription: 'Perform assessments offline',
          icon: ICONS.ArrowDownLight,
          type: PageTypeEnum.page,
          closeable: false,
          config: {
            type: PageEnum.downloadedProperties,
            data: {},
          },
        },
      ];
      if (hasAssessments) {
        pages.push({
          id: 'assessments',
          title: 'Assessments',
          description: '',
          menuDescription: 'Quality check and approve assessments',
          icon: ICONS.FileLight,
          type: PageTypeEnum.page,
          closeable: false,
          config: {
            type: PageEnum.assessments,
            data: {},
          },
        });
      }
      yield* put(
        doReportingSetSavedPages({
          pages,
        }),
      );
    }

    yield* put(SET_APP_INSTANCE(yield* select(selectAppInstance)));

    yield delay(500);
    const userId = yield* select(selectUserId);
    if (userId) {
      sentrySetUser({
        id: userId,
      });
    }
    if (!(yield* select(selectUserId))) {
      yield* put(doAuthShowAuthModal({ actionAfterAuth: undefined }));
    }
    yield* put(INIT_BG_LOOP());
  }),
  takeEvery(RESET_DEV, function* f() {
    yield* put(SHOW_BLOCKING_LOADER());
    navigationRef.current?.reset({
      index: 0,
      routes: [{ name: Screens.RootPage }],
    });
    yield delay(500);
    yield* put(RESET_DEV_DATA());
    yield* put(HIDE_BLOCKING_LOADER());
    // eslint-disable-next-line no-console
    console.info('RESET');
  }),
  takeEvery(ALERT_ERROR, function* f(action) {
    const { error } = action.payload;
    let errorShown = false;
    if (isNetworkError(error)) {
      if (
        error.message === 'ClientOutOfDateMinor' ||
        error.message === 'ClientOutOfDateMajor'
      ) {
        yield* put(
          NAVIGATE_MODAL({
            screen: ModalScreens.UpgradeRequired,
            params: {
              major: error.message === 'ClientOutOfDateMajor',
              withClose: true,
            },
          }),
        );
        errorShown = true;
      } else if (error.isOffline) {
        // Show offline message
        yield* put(
          ALERT({
            text: error.message,
            type: 'networkerror',
          }),
        );
        errorShown = true;
      } else if (error.isUnauthorized) {
        // Show auth and fire action after auth
        yield* put(
          doAuthShowAuthModal({ actionAfterAuth: action.payload.action }),
        );
        errorShown = true;
      } else if (error.isTooManyRequests) {
        // Show too many requests message
        yield* put(
          ALERT({
            title: 'Too Many Requests',
            text:
              'The app has made too many requests in a short period of time.\n\n' +
              'Please try again in a few minutes.',
            type: 'networkerror',
            altButton: {
              action: NAVIGATE_MODAL({
                screen: ModalScreens.Support,
                params: {},
              }),
              text: 'Contact Support',
            },
          }),
        );
        errorShown = true;
      }
    }
    if (!errorShown) {
      yield* put(
        ALERT({
          text: error.message,
        }),
      );
    }
  }),
  takeLatest(DO_NETWORK_CHECK_UNTIL_ONLINE, function* f(action): Generator {
    if (action.payload) {
      if (
        !(yield* call(checkInternetConnection, {
          throwErrorIfOffline: false,
          forcePing: true,
          doPingIfWeb: true,
        }))
      ) {
        yield delay(CHECK_OFFLINE_DELAY);
        yield* put(DO_NETWORK_CHECK_UNTIL_ONLINE(true));
      }
    }
  }),
  takeEvery(DO_IMAGE_SELECT, function* f(action) {
    const { action: actionAfter, source } = action.payload;
    try {
      if (source === FileSourceType.IMAGE) {
        // const permission = yield* call(getMediaLibraryPermission);
        // if (!permission.granted) {
        //   yield* put(
        //     ALERT({
        //       text:
        //         Platform.OS === 'ios'
        //           ? 'To add photos, please give RiskBase access to "All Photos" in settings'
        //           : 'To add photos, please give RiskBase access to photos in settings',
        //     }),
        //   );
        //   return;
        // }
        // if (permission.limited) {
        //   yield* put(
        //     ALERT({
        //       text:
        //         'It looks like you\'ve only given RiskBase access to "Selected Photos"\n\n' +
        //         'To add photos, please give RiskBase access to "All Photos" in settings',
        //       altButton: {
        //         text: 'Open Settings',
        //         action: OPEN_NATIVE_APP_SETTINGS(),
        //       },
        //     }),
        //   );
        //   return;
        // }
      } else {
        const permission = yield* call(hasCameraPermission);
        if (!permission) {
          yield* put(
            ALERT({
              text: 'To take photos, please give RiskBase access to your camera in settings',
              altButton: {
                text: 'Open Settings',
                action: OPEN_NATIVE_APP_SETTINGS(),
              },
            }),
          );
          return;
        }
      }

      const saveToMediaLibrary =
        source === FileSourceType.CAMERA &&
        !!(yield* select(selectSettingSavePhotosToMediaLibrary));

      const files = yield* {
        [FileSourceType.CAMERA]: () =>
          call(getPhoto, {
            saveToMediaLibrary,
          }),
        [FileSourceType.IMAGE]: () => call(getImage),
      }[source]();

      const file = files[0];
      if (file) {
        yield* put({
          ...actionAfter,
          payload: {
            ...actionAfter.payload,
            image: removeDataBase64Prefix(
              (yield* call(resizeImage, {
                uri: file.uri || (yield* call(readAsDataURLAsync, file.file!)),
                width: file.width,
                height: file.height,
                maxWidth: IMAGE_MAX_WIDTH,
                maxHeight: IMAGE_MAX_HEIGHT,
                compression: IMAGE_COMPRESSION,
              })).base64,
            ),
          },
        });
      }
    } catch (error) {
      yield* put(
        CATCH_ERROR({
          error,
          info: { _: 'doImageSelectSaga', actionAfter },
        }),
      );
      yield* put(
        ALERT({
          text: "We couldn't use that image. The type of file might not be supported.",
        }),
      );
    }
  }),

  takeEvery(OPEN_BARCODE_VIEWER, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.QR_VIEWER,
        params: {
          data: action.payload.data,
          deleteKey: action.payload.deleteKey,
        },
      }),
    );
  }),

  takeEvery(OPEN_BARCODE_SCANNER, function* f() {
    let error: string | undefined;

    if (Platform.OS === 'web') {
      if (!(yield* call(CameraView.isAvailableAsync))) {
        error =
          "A camera wasn't found, check your device has a camera and try again";
      }
    } else {
      const hasPermission = yield* call(hasCameraPermission);
      if (!hasPermission) {
        error =
          'Please give RiskBase access to your camera in settings to use this feature';
      }
    }

    if (error) {
      yield* put(
        ALERT({
          text: error,
        }),
      );
      yield* put(UNLOAD_BARCODE_SCANNER());
      return;
    }

    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.QR_SCANNER,
        params: {},
      }),
    );
  }),

  takeEvery(CLOSE_BARCODE_SCANNER, function* f(action) {
    const barCodeScanner = yield* select(selectBarCodeScanner);
    yield* put(UNLOAD_BARCODE_SCANNER());
    yield* put(NAVIGATE_MODAL_BACK());
    if (barCodeScanner && action.payload.data) {
      // Use delay, this allows navigation to run successfully
      yield delay(0);
      if (barCodeScanner.actionAfter) {
        yield* put({
          ...barCodeScanner.actionAfter,
          payload: {
            ...barCodeScanner.actionAfter.payload,
            data: action.payload.data,
          },
        });
      }
      if (barCodeScanner.selectionKey) {
        yield* put(
          eventBarcodeSelected({
            key: barCodeScanner.selectionKey,
            data: action.payload.data,
          }),
        );
      }
    }
  }),

  takeEvery(NAVIGATE_MODAL, function* f(action) {
    if (Platform.OS === 'ios' && action.payload.params) {
      yield* call(hapticSelection);
      navigationRef.current?.navigate(
        /** @ts-expect-error this can be navigated to */
        action.payload.screen,
        action.payload.params,
      );
    }
  }),

  takeEvery(NAVIGATE_MODAL_BACK, function* f() {
    if (Platform.OS === 'ios') {
      const discardedModal = yield* select(selectModalsDiscarded);
      if (discardedModal) {
        navigationRef.current?.goBack();
      }
    }
  }),

  takeEvery(OPEN_NATIVE_APP_SETTINGS, function* f() {
    if (Platform.OS !== 'web') {
      yield* call(Linking.openSettings);
    }
  }),

  takeEvery(doFileShare, function* f(action) {
    const { url, post, method } = action.payload;
    const loaderKey = randomKey();
    const loader2Key = randomKey();
    // Web
    if (Platform.OS === 'web') {
      yield* put(SHOW_BLOCKING_LOADER(loaderKey));
      try {
        const response =
          method === 'POST'
            ? yield* apiPost<EPFileResponse>(url, post)
            : yield* apiGet<EPFileResponse>(url);
        yield* put(HIDE_BLOCKING_LOADER(loaderKey));
        if (response.poll) {
          yield* call(pollForFile, response);
        }
        if (typeof window !== 'undefined') {
          window.open(
            API_FILE_GENERIC(response.filename, true),
            '_blank',
            'noopener',
          );
        }
      } catch (error) {
        yield* put(HIDE_BLOCKING_LOADER(loaderKey));
        if (!isCancelled(error)) {
          if (isError(error)) {
            yield* put(ALERT_ERROR({ error }));
          }
          yield* put(CATCH_ERROR({ error }));
        }
      }
      return;
    }
    // Native
    yield* put(SHOW_BLOCKING_LOADER(loaderKey));
    try {
      const response =
        method === 'POST'
          ? yield* apiPost<EPFileResponse>(url, post)
          : yield* apiGet<EPFileResponse>(url);
      yield* put(HIDE_BLOCKING_LOADER(loaderKey));
      if (response.poll) {
        yield* call(pollForFile, response);
      }
      yield* put(doShowLoading({ text: 'Downloading', key: loader2Key }));
      const downloadedFile = yield* call(
        FileSystem.File.downloadFileAsync,
        API_FILE_GENERIC(response.filename),
        new FileSystem.File(
          FileSystem.Paths.cache,
          sanitizeFilename(response.title, { extension: response.extension }) +
            `.${response.extension}`,
        ),
        {
          idempotent: true,
        },
      );
      yield* put(HIDE_BLOCKING_LOADER(loader2Key));
      if (!downloadedFile.exists) {
        throw new Error("File couldn't be downloaded");
      }
      yield* call(Sharing.shareAsync, downloadedFile.uri, {
        dialogTitle: response.title,
        UTI: 'public.item',
      });
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER(loaderKey));
      yield* put(HIDE_BLOCKING_LOADER(loader2Key));
      if (!isCancelled(error)) {
        if (isError(error)) {
          yield* put(ALERT_ERROR({ error }));
        }
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),

  takeEvery(doFileShareDirect, function* f(action) {
    const { url, title, fileType } = action.payload;
    // Web
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener');
      }
      return;
    }
    // Native
    const loaderKey = randomKey();
    yield* put(SHOW_BLOCKING_LOADER(loaderKey));
    try {
      const downloadedFile = yield* call(
        FileSystem.File.downloadFileAsync,
        url,
        new FileSystem.File(
          FileSystem.Paths.cache,
          sanitizeFilename(title, {
            extension: fileType.extension,
          }) + `.${fileType.extension}`,
        ),
        {
          idempotent: true,
        },
      );
      yield* put(HIDE_BLOCKING_LOADER(loaderKey));
      if (!downloadedFile.exists) {
        throw new Error("File couldn't be downloaded");
      }
      yield* call(Sharing.shareAsync, downloadedFile.uri, {
        dialogTitle: title,
        mimeType: fileType.mime,
        UTI: fileType.uti,
      });
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER(loaderKey));
      if (!isCancelled(error)) {
        if (isError(error)) {
          yield* put(ALERT_ERROR({ error }));
        }
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),

  takeEvery(doFilePreviewPdf, function* f(action) {
    const { url } = action.payload;
    const loaderKey = randomKey();
    yield* put(SHOW_BLOCKING_LOADER(loaderKey));
    try {
      const response = yield* apiGet<EPFileResponse>(url);
      yield* put(HIDE_BLOCKING_LOADER(loaderKey));
      if (response.poll) {
        yield* call(pollForFile, response);
      }
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.PDFVIEWER,
          params: {
            title: response.title,
            sourceUrl: API_FILE_GENERIC(response.filename),
          },
        }),
      );
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER(loaderKey));
      if (!isCancelled(error)) {
        if (isError(error)) {
          yield* put(ALERT_ERROR({ error }));
        }
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),

  takeLatest(doSetSettingSavePhotosToMediaLibrary, function* f(action) {
    const { enable, ask } = action.payload;
    if (enable && ask) {
      const permission = yield* call(MediaLibrary.requestPermissionsAsync);
      if (permission.status !== MediaLibrary.PermissionStatus.GRANTED) {
        yield* put(
          ALERT({
            text:
              Platform.OS === 'ios'
                ? `To enable this feature, please give RiskBase "Full Access" to your photos in your device${str.apos}s settings`
                : `To enable this feature, please give RiskBase access to photos in your device${str.apos}s settings`,
            altButton: {
              text: 'Open Settings',
              action: OPEN_NATIVE_APP_SETTINGS(),
            },
          }),
        );
        return;
      }
    }
    yield* put(
      doAppMerge({
        settingSavePhotosToMediaLibrary: enable,
      }),
    );
  }),

  takeLatest(doShowSyncModal, function* f() {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.Sync,
        params: {},
      }),
    );
  }),
  takeLatest(doHideSyncModal, function* f() {
    yield* put(NAVIGATE_MODAL_BACK([ModalScreens.Sync]));
  }),

  takeEvery(doAppFileGetFilesFromPicker, function* f(action) {
    try {
      const { returnType, assetId, eventKey, multiSelect } = action.payload;
      const files = yield* call(getDocument, { multiple: multiSelect });
      // If the user cancels selection, we won't get files returned
      if (files.length) {
        if (returnType === 'saveToAsset') {
          yield* put(
            doAssetSaveFilesToAsset({
              assetId,
              files,
              eventKey,
              source: FileSourceType.FILE,
            }),
          );
        } else {
          yield* put(
            eventFilesAdded({
              key: eventKey,
              files,
            }),
          );
        }
      }
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doAppFileGetImagesFromPicker, function* f(action) {
    try {
      const { returnType, assetId, eventKey, multiSelect } = action.payload;
      const files = yield* call(getImage, { multiple: multiSelect });
      // If the user cancels selection, we won't get images returned
      if (files.length) {
        if (returnType === 'saveToAsset') {
          yield* put(
            doAssetSaveFilesToAsset({
              assetId,
              files,
              eventKey,
              source: FileSourceType.IMAGE,
            }),
          );
        } else {
          yield* put(
            eventFilesAdded({
              key: eventKey,
              files,
            }),
          );
        }
      }
    } catch (error) {
      yield* put(doLoaderCancel({ key: 'getImagesFromPicker' }));
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),
];
